import { ref, readonly, onMounted, onUnmounted } from 'vue'
import { logger, createLogger } from '@/utils/logger'

const fileLogger = createLogger('useNetworkStatus')

export interface NetworkStatus {
  isOnline: boolean
  isSlowConnection: boolean
  connectionType: string | null
  lastOnlineTime: number | null
  retryCount: number
}

export function useNetworkStatus() {
  const networkStatus = ref<NetworkStatus>({
    isOnline: navigator.onLine,
    isSlowConnection: false,
    connectionType: null,
    lastOnlineTime: null,
    retryCount: 0
  })

  const checkConnectionSpeed = async (): Promise<boolean> => {
    try {
      const startTime = performance.now()
      
      // Try to fetch a small resource to test connection speed
      const faviconUrl = new URL('favicon.png', window.location.origin + import.meta.env.BASE_URL).href
      const response = await fetch(faviconUrl, {
        method: 'HEAD',
        cache: 'no-cache',
        signal: AbortSignal.timeout(5000) // 5 second timeout
      })
      
      const endTime = performance.now()
      const duration = endTime - startTime
      
      // Consider connection slow if it takes more than 2 seconds
      const isSlow = duration > 2000
      
      fileLogger.log(`Connection speed test: ${duration.toFixed(2)}ms (${isSlow ? 'slow' : 'fast'})`)
      return isSlow
    } catch (error) {
      fileLogger.warn('Connection speed test failed:', error)
      return true // Assume slow if test fails
    }
  }

  const updateNetworkStatus = async () => {
    const wasOnline = networkStatus.value.isOnline
    const isOnline = navigator.onLine
    
    networkStatus.value.isOnline = isOnline
    
    if (isOnline && !wasOnline) {
      // Just came back online
      networkStatus.value.lastOnlineTime = Date.now()
      networkStatus.value.retryCount = 0
      
      // Test connection speed when coming back online
      networkStatus.value.isSlowConnection = await checkConnectionSpeed()
      
      fileLogger.log('Network connection restored')
    } else if (!isOnline && wasOnline) {
      // Just went offline
      fileLogger.warn('Network connection lost')
    } else if (isOnline) {
      // Still online, periodically check speed
      networkStatus.value.isSlowConnection = await checkConnectionSpeed()
    }
  }

  const incrementRetryCount = () => {
    networkStatus.value.retryCount++
  }

  const resetRetryCount = () => {
    networkStatus.value.retryCount = 0
  }

  const handleOnline = () => {
    fileLogger.log('Online event detected')
    updateNetworkStatus()
  }

  const handleOffline = () => {
    fileLogger.log('Offline event detected')
    updateNetworkStatus()
  }

  let speedCheckInterval: ReturnType<typeof setInterval> | null = null

  onMounted(async () => {
    await updateNetworkStatus()

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    speedCheckInterval = setInterval(async () => {
      if (networkStatus.value.isOnline) {
        await updateNetworkStatus()
      }
    }, 30000)
  })

  onUnmounted(() => {
    window.removeEventListener('online', handleOnline)
    window.removeEventListener('offline', handleOffline)
    if (speedCheckInterval) {
      clearInterval(speedCheckInterval)
    }
  })

  return {
    networkStatus: readonly(networkStatus),
    updateNetworkStatus,
    incrementRetryCount,
    resetRetryCount,
    checkConnectionSpeed
  }
}
